import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState<string>(slug || 'fanwen-jingxuan');

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === activeCategory),
    [activeCategory]
  );

  const categoryArticles = useMemo(
    () => articles.filter((a) => a.categorySlug === activeCategory),
    [activeCategory]
  );

  const itemListSchema = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: categoryArticles.map((article, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'Article',
        name: article.title,
        url: `https://zuowenjiqiao.com/article/${article.slug}`,
      },
    })),
  });

  return (
    <>
      <title>{currentCategory ? `${currentCategory.name} - 写作技巧大全` : '分类页面 - 写作技巧大全'}</title>
      <meta
        name="description"
        content={currentCategory?.description || '按分类浏览作文范文与写作技巧'}
      />
      <link rel="canonical" href={`https://zuowenjiqiao.com/category/${activeCategory}`} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 pb-10 md:pb-14 px-4 md:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: currentCategory?.name || '分类' },
                ]}
              />
            </div>

            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl font-heading text-foreground-900">
                {currentCategory?.name || '分类页面'}
              </h1>
              <p className="text-sm md:text-base text-foreground-600 mt-2">
                {currentCategory?.description}
              </p>
            </div>

            <div className="flex gap-2 mb-8 overflow-x-auto pb-2 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveCategory(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200 border border-background-200/70'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {categoryArticles.length > 0 ? (
              <div className="space-y-3 md:space-y-4">
                {categoryArticles.map((article) => (
                  <ArticleCard key={article.slug} article={article} variant="default" />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500 mb-4">
                  <i className="ri-inbox-line text-2xl"></i>
                </div>
                <p className="text-foreground-600">该分类暂无文章</p>
              </div>
            )}

            <script type="application/ld+json">{itemListSchema}</script>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}